// Asteroidi vicini: testi comuni (distanza, confronto con la Luna, grandezza) per Eventi ed Esplora, e i calcoli
// fatti sul dispositivo dell'utente con la traiettoria della NASA: posizione in un istante, luminosità apparente,
// «dove guardare» da un luogo. La posizione dell'utente non esce mai dal browser.

import { degreesToRadians, ecfToLookAngles, eciToEcf, gstime, jday, sunPos } from 'satellite.js';
import type { TraiettoriaAsteroide } from '../../../ingest/tipi';
import { n } from './formato';
import { t } from '../i18n';

export const KM_LUNA = 384_400;
const KM_UA = 149_597_870.7;
const GRADI = 180 / Math.PI;

/** «222.000 km» sotto il milione, «4,9 milioni di km» sopra. */
export function km(x: number): string {
  return x < 1e6 ? `${n(Math.round(x / 1000) * 1000)} km` : t().eventi.milioniKm(n(x / 1e6, 1));
}
/** «più vicino della Luna» oppure «13 volte la distanza della Luna». */
export function rispettoLuna(dl: number): string {
  const e = t().eventi;
  return dl < 1 ? e.piuVicinoLuna : e.volteLuna(n(dl, dl < 10 ? 1 : 0));
}
/** Grandezza con un paragone di tutti i giorni, dal diametro medio stimato. */
export function grandezza(d: [number, number]): string {
  const e = t().eventi;
  const m = (d[0] + d[1]) / 2;
  const soglie = [5, 15, 30, 60, 120, 300];
  const i = soglie.findIndex((s) => m < s);
  return e.comeUn(e.oggetti[i === -1 ? soglie.length : i]);
}

/** Posizione geocentrica (km, equatoriale) all'istante t, interpolata fra i punti di Horizons; null fuori intervallo. */
export function posizioneA(a: TraiettoriaAsteroide, ms: number): [number, number, number] | null {
  const p = a.punti;
  if (!p.length || ms < p[0][0] || ms > p[p.length - 1][0]) return null;
  let i = Math.min(p.length - 2, Math.floor(((ms - p[0][0]) / (p[p.length - 1][0] - p[0][0])) * (p.length - 1)));
  while (i > 0 && p[i][0] > ms) i--;
  while (i < p.length - 2 && p[i + 1][0] < ms) i++;
  const [t0, x0, y0, z0] = p[i], [t1, x1, y1, z1] = p[i + 1];
  const k = (ms - t0) / (t1 - t0 || 1);
  return [x0 + (x1 - x0) * k, y0 + (y1 - y0) * k, z0 + (z1 - z0) * k];
}

const lunghezza = (v: number[]) => Math.hypot(v[0], v[1], v[2]);

/** Sole geocentrico in km (equatoriale) all'istante dato. */
function sole(ms: number): [number, number, number] {
  const r = sunPos(jday(new Date(ms))).rsun;
  return [r.x * KM_UA, r.y * KM_UA, r.z * KM_UA];
}

/** Magnitudine apparente (sistema H-G, G = 0,15) vista dalla Terra all'istante dato. */
export function magnitudine(a: TraiettoriaAsteroide, ms: number): number | null {
  const pos = posizioneA(a, ms);
  if (!pos || a.h === null) return null;
  const s = sole(ms);
  const versoSole = [s[0] - pos[0], s[1] - pos[1], s[2] - pos[2]];
  const versoTerra = [-pos[0], -pos[1], -pos[2]];
  const r = lunghezza(versoSole) / KM_UA, delta = lunghezza(versoTerra) / KM_UA;
  const cosAlfa = (versoSole[0] * versoTerra[0] + versoSole[1] * versoTerra[1] + versoSole[2] * versoTerra[2]) / (lunghezza(versoSole) * lunghezza(versoTerra));
  const tg = Math.tan(Math.acos(Math.max(-1, Math.min(1, cosAlfa))) / 2);
  const fase = 0.85 * Math.exp(-3.33 * tg ** 0.63) + 0.15 * Math.exp(-1.87 * tg ** 1.22);
  return a.h + 5 * Math.log10(r * delta) - 2.5 * Math.log10(fase);
}

/** Con che strumento si vede: 0 occhio nudo … 4 solo telescopio con fotocamera. */
export const strumento = (m: number) => (m < 6 ? 0 : m < 9 ? 1 : m < 12 ? 2 : m < 14 ? 3 : 4);

export interface Osservazione { ms: number; elevazione: number; azimut: number; magnitudine: number | null }

/** Momento migliore per osservare da un luogo nelle 24 ore attorno al passaggio: asteroide alto almeno 10° e cielo
 *  buio (Sole sotto −12°), il più luminoso. null se da lì non si vede mai a cielo buio. */
export function momentoMigliore(a: TraiettoriaAsteroide, lat: number, lon: number): Osservazione | null {
  const centro = new Date(a.data).getTime();
  const oss = { latitude: degreesToRadians(lat), longitude: degreesToRadians(lon), height: 0 };
  let migliore: Osservazione | null = null;
  for (let ms = centro - 12 * 3600_000; ms <= centro + 12 * 3600_000; ms += 10 * 60_000) {
    const pos = posizioneA(a, ms);
    if (!pos) continue;
    const gmst = gstime(new Date(ms));
    const v = ecfToLookAngles(oss, eciToEcf({ x: pos[0], y: pos[1], z: pos[2] }, gmst));
    const s = sole(ms);
    const vs = ecfToLookAngles(oss, eciToEcf({ x: s[0], y: s[1], z: s[2] }, gmst));
    const el = v.elevation * GRADI;
    if (el < 10 || vs.elevation * GRADI > -12) continue;
    const m = magnitudine(a, ms);
    if (!migliore || (m ?? 99) < (migliore.magnitudine ?? 99)) migliore = { ms, elevazione: el, azimut: ((v.azimuth * GRADI) + 360) % 360, magnitudine: m };
  }
  return migliore;
}
